import React, { useState } from "react";
import { RefreshCw, Loader2, Wand2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { regenerateCopy } from "../lib/copyAdaptApi";
import { formatError } from "../lib/api";

const PRESET_TWEAKS = [
  "Punchier",
  "Warmer, encouraging",
  "No analogies",
  "Shorter",
  "One sentence only",
  "More concrete, less abstract",
];

/**
 * RegenerateChip — hover-visible small button that opens a popover for
 * regenerating an adapted passage with feedback. Meant to be positioned
 * absolutely inside any parent that has `position: relative`.
 *
 * Props:
 *   source, sourceId, field, level, originalText  — required to hit the API
 *   onDone(adaptedText)                            — called with the new text on success
 *   className                                       — extra classes on the trigger
 *   align: 'right' | 'left'  — popover alignment
 */
export default function RegenerateChip({
  source, sourceId, field, level, originalText,
  onDone, className = "", align = "right",
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  if (level === "insider" || !originalText) return null;

  const submit = async (nudge) => {
    const patch = (nudge || feedback || "").trim();
    if (!patch && !nudge) return;
    setBusy(true);
    try {
      const res = await regenerateCopy({
        source, sourceId, field, level,
        originalText, feedback: patch,
      });
      onDone && onDone(res.adapted || originalText);
      toast.success("Regenerated");
      setOpen(false);
      setFeedback("");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Regenerate with feedback"
        className="w-7 h-7 rounded-full border border-shell-border/70 bg-white/95 shadow-sm inline-flex items-center justify-center text-muted-foreground hover:border-shell-accent hover:text-shell-accent transition"
        data-testid={`regen-chip-${field}`}
      >
        <RefreshCw className="w-3.5 h-3.5" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={`absolute top-9 z-50 w-72 rounded-xl border border-shell-border bg-white shadow-2xl overflow-hidden ${
              align === "right" ? "right-0" : "left-0"
            }`}
            data-testid={`regen-popover-${field}`}
          >
            <div className="px-4 py-3 border-b border-shell-border/60 bg-muted/40 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-shell-accent font-semibold inline-flex items-center gap-1">
                  <Wand2 className="w-3 h-3" /> Regenerate
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Nudge the tone. Overwrites the cached version.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-muted"
                aria-label="Close regenerate popover"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="px-4 py-3">
              <div className="flex flex-wrap gap-1.5 mb-3">
                {PRESET_TWEAKS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => submit(t)}
                    disabled={busy}
                    className="text-[11px] px-2 py-1 rounded-full border border-shell-border/70 hover:border-shell-accent hover:text-shell-accent disabled:opacity-40"
                    data-testid={`regen-preset-${t.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <textarea
                rows={2}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Your own guidance… e.g. drop the acronyms"
                className="w-full text-xs rounded-md border border-border bg-white px-2 py-1.5 outline-none focus:border-shell-accent resize-y"
                data-testid={`regen-feedback-${field}`}
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => submit()}
                  disabled={busy || !feedback.trim()}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-shell-accent text-white font-semibold hover:opacity-90 disabled:opacity-40"
                  data-testid={`regen-submit-${field}`}
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
